import { Schedule as ScheduleIcon, FilterList as FilterListIcon } from "@mui/icons-material"
import ClearIcon from "@mui/icons-material/Clear"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SearchIcon from "@mui/icons-material/Search"
import {
  TextField,
  IconButton,
  InputAdornment,
  Button,
  MenuItem,
  Popper,
  MenuList,
  Paper,
  Grow,
  ClickAwayListener,
  Box,
} from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress"
import Typography from "@mui/material/Typography"
import { localTimezone, MomentFormats } from "app-constants/constants"
import { useFiltersBarStyles } from "components/filters-forms/filters-bar-styles"
import useToggle from "components/use-toggle/use-toggle"
import HiddenBox from "components/HiddenBox"
import { api } from "lib/api"
import { Convention } from "lib/types"
import useCurrent from "lib/use-current"
import { useStateMachine } from "little-state-machine"
import moment from "moment-timezone"
import { useRouter } from "next/router"
import { switcher } from "rambdax"
import React, { Dispatch, SetStateAction, useRef, useState, useEffect, useMemo } from "react"
import { updateSessionsFilters } from "stores/sessions-filters-store"

interface ISessionsFiltersBar {
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>
  isDrawerOpen: boolean
  filtersCount: number
  resultsCount: number
}

interface IFilters extends ISessionsFiltersBar {
  classes: any
  timeRangeFilterRef: any
}

interface TimeRangeFilterOption {
  label: string
  value: string
  timeRange: boolean
  icon: React.ReactNode
  actionable: boolean
}

const Filters = (props: IFilters) => {
  const { classes, timeRangeFilterRef } = props

  const [timeRangeFilterOptions, setTimeRangeFilterOptions] = useState<
    TimeRangeFilterOption[] | undefined
  >(undefined)
  const [isTimeRangeFilterOpen, setIsTimeRangeFilterOpen] = useState(false)
  const [convention, setConvention] = useState<Convention>()

  const { actions, state } = useStateMachine({ updateSessionsFilters })

  const selectedTimeRangeFilterIndex = useMemo(() => {
    if (!timeRangeFilterOptions) return undefined
    return timeRangeFilterOptions.findIndex(({ value, timeRange }) => {
      if (timeRange) return value === state.sessionsFiltersStore.when_time
      return value === state.sessionsFiltersStore.when
    })
  }, [timeRangeFilterOptions, state.sessionsFiltersStore.when_time])

  // @ts-ignore
  const { config: providerConfig } = useCurrent()

  const handleTimeRangeFilterClick = () => {
    setIsTimeRangeFilterOpen(true)
  }

  // @ts-ignore PT - #180597146
  const handleTimeRangeFilterClose = (event) => {
    if (timeRangeFilterRef.current && timeRangeFilterRef.current.contains(event.target)) {
      return
    }
    setIsTimeRangeFilterOpen(false)
  }
  // @ts-ignore PT - #180597146
  const handleTimeRangeFilterMenuItemClick = (index) => {
    // @ts-ignore
    const selectedActionValue = timeRangeFilterOptions[index].value
    // @ts-ignore
    const isTimeRange = timeRangeFilterOptions[index].timeRange

    const timeRangeFilterSessions = async () => {
      const when = selectedActionValue ? selectedActionValue : ""

      if (isTimeRange) {
        actions.updateSessionsFilters({
          when_time: when,
          when: "",
        })
      } else {
        actions.updateSessionsFilters({
          when_time: "",
          when: when,
        })
      }
    }

    const timeRangeFilterMethod = switcher<Function>(selectedActionValue)
      // .is("Foo", timeRangeFilterSessions({ timeStart: selectedAction }))
      .default(timeRangeFilterSessions)

    !!timeRangeFilterMethod && timeRangeFilterMethod()

    setIsTimeRangeFilterOpen(false)
  }

  const router = useRouter()

  useEffect(() => {
    // Convention Data
    const fetchEventAndSessionData = async () => {
      const { data: convention } = await api.getEvent(
        { req: undefined },
        // @ts-ignore
        router.query["convention_id"],
      )

      const { data } = await api.getSessionAvailableHours(
        { req: undefined },
        // @ts-ignore
        router.query["convention_id"],
      )
      const sessionTimeRanges = data.attributes.starting_hours

      const timeRanges = sessionTimeRanges.map((timeRange: any) => {
        // wrapping in moment so it's instantiated or cloned if already a moment
        // all moments are mutable
        const startAt = moment.utc(timeRange).tz(localTimezone)
        const endAt = moment(startAt).add(1, "hour")

        const startTime = startAt.format(MomentFormats.timeOnly)
        const endTime = endAt.format(MomentFormats.timeOnlyWithZone)

        const label = `Starting ${startTime} - ${endTime}`

        return {
          groupingKey: startAt.format(MomentFormats.dateOnly),
          groupingLabel: startAt.format(MomentFormats.dateOnlyWithoutYear),
          label: label,
          value: startAt.toISOString(),
          icon: <ScheduleIcon />,
        }
      })

      const isMultiDayConvention =
        timeRanges
          // @ts-ignore PT - #180597146
          .map((timeRange) => timeRange.groupingKey)
          // @ts-ignore PT - #180597146
          .filter((val, ix, cur) => {
            return cur.indexOf(val) === ix
          }).length > 1

      const timeRangeGroups = {}
      // @ts-ignore PT - #180597146
      const fullListOfTimeRangesWithGroups = []

      // Adding timeRange key to distinguish b/w
      // "when_time" & "when" filter param key

      // @ts-ignore PT - #180597146
      timeRanges.forEach((timeRange) => {
        // @ts-ignore PT - #180597146
        if (isMultiDayConvention && !timeRangeGroups[timeRange.groupingKey]) {
          // @ts-ignore PT - #180597146
          timeRangeGroups[timeRange.groupingKey] = true
          fullListOfTimeRangesWithGroups.push({
            // @ts-ignore
            key: timeRange.groupingKey,
            // @ts-ignore
            label: timeRange.groupingLabel,
            // @ts-ignore
            timeRange: true,
            // @ts-ignore
            icon: null,
            // @ts-ignore
            actionable: false,
          })
        }

        fullListOfTimeRangesWithGroups.push({
          // @ts-ignore
          key: `${timeRange.groupingKey}|${timeRange.label}`,
          // @ts-ignore
          label: timeRange.label,
          // @ts-ignore
          value: timeRange.value,
          // @ts-ignore
          timeRange: true,
          // @ts-ignore
          icon: timeRange.icon,
          // @ts-ignore
          actionable: true,
        })
      })

      const trFilterOptions = [
        {
          label: "All Times",
          value: "now_and_upcoming",
          timeRange: false,
          icon: <ScheduleIcon />,
          actionable: true,
        },
        // @ts-ignore PT - #180597146
        ...fullListOfTimeRangesWithGroups,
      ]

      setConvention(convention)
      // @ts-ignore
      setTimeRangeFilterOptions(trFilterOptions)
    }
    fetchEventAndSessionData()
  }, [])

  if (!timeRangeFilterOptions || !convention || selectedTimeRangeFilterIndex === undefined) {
    return (
      <Box className={classes.circularProgressContainer}>
        <CircularProgress variant="indeterminate" />
      </Box>
    )
  }

  return (
    <Box
      display="flex"
      className={classes.filtersRightGroup}
      justifyContent="flex-end"
      width="100%">
      <TextField
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: !!state.sessionsFiltersStore.exhibitorName ? (
            <InputAdornment position="end">
              <IconButton
                onClick={() =>
                  actions.updateSessionsFilters({
                    exhibitorName: "",
                  })
                }
                aria-label="clear search bar"
                size="large">
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        autoFocus
        size="medium"
        variant="outlined"
        placeholder={`Search by ${providerConfig.exhibitor_copy_text}`}
        value={state.sessionsFiltersStore.exhibitorName}
        onChange={(event) =>
          actions.updateSessionsFilters({
            exhibitorName: event.target.value,
          })
        }
      />

      {selectedTimeRangeFilterIndex !== -1 && (
        <>
          <Button
            variant="outlined"
            size="large"
            ref={timeRangeFilterRef}
            classes={{
              root: classes.sortButton,
            }}
            // @ts-ignore
            startIcon={timeRangeFilterOptions[selectedTimeRangeFilterIndex].icon}
            onClick={handleTimeRangeFilterClick}>
            {/*// @ts-ignore*/}
            {timeRangeFilterOptions[selectedTimeRangeFilterIndex].label}
          </Button>

          <Popper
            open={isTimeRangeFilterOpen}
            anchorEl={timeRangeFilterRef.current}
            role={undefined}
            transition
            style={{
              zIndex: 10,
            }}
            disablePortal>
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === "bottom" ? "center top" : "center bottom",
                }}>
                <Paper>
                  <ClickAwayListener onClickAway={handleTimeRangeFilterClose}>
                    <MenuList id="split-button-menu">
                      {/*// @ts-ignore*/}
                      {timeRangeFilterOptions.map((option, index) => (
                        <MenuItem
                          key={option.value}
                          selected={index === selectedTimeRangeFilterIndex}
                          disabled={!option.actionable}
                          onClick={() =>
                            option.actionable ? handleTimeRangeFilterMenuItemClick(index) : {}
                          }>
                          {option.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </>
      )}
    </Box>
  )
}

const DesktopFilters = (props: ISessionsFiltersBar) => {
  const { classes, cx } = useFiltersBarStyles({ mobileFiltersExpanded: false })

  const timeRangeFilterRef = useRef(null)

  // @ts-ignore
  const { config: providerConfig } = useCurrent()

  return (
    <div className={cx(classes.searchTools, classes.hideMobile)}>
      <Box display="flex" className={classes.filtersRoot} width="100%">
        <Filters {...props} classes={classes} timeRangeFilterRef={timeRangeFilterRef} />
      </Box>
    </div>
  )
}

const MobileFilters = (props: ISessionsFiltersBar) => {
  const [mobileFiltersExpanded, toggleMobileFilters] = useToggle(false)

  const { classes, cx } = useFiltersBarStyles({ mobileFiltersExpanded })

  // @ts-ignore
  const { config: providerConfig } = useCurrent()

  const timeRangeFilterRef = useRef(null)

  return (
    <>
      <div className={cx(classes.searchToolsToggler, classes.hideDesktop)}>
        <button onClick={toggleMobileFilters as any} className={classes.expandMoreWrapper}>
          <Typography variant="body2">Search Tools</Typography>
          <div className={classes.expandMoreIcon}>
            <ExpandMoreIcon />
          </div>
        </button>
      </div>
      {mobileFiltersExpanded && (
        <div className={classes.expandedMobileFilters}>
          <Box display="flex" className={classes.filtersRoot} width="100%">
            <Box width="100%">
              <Box
                display="flex"
                alignItems="center"
                className={classes.mobileFiltersToggleWrapper}>
                <div className={classes.mobileFiltersToggle}>
                  <Button
                    variant="outlined"
                    size="large"
                    ref={timeRangeFilterRef}
                    onClick={() => {
                      props.setIsDrawerOpen(true)
                    }}
                    classes={{
                      root: classes.mobileFilterButton,
                    }}
                    startIcon={
                      <FilterListIcon color={props.filtersCount > 0 ? "primary" : "inherit"} />
                    }>
                    Filters
                  </Button>
                </div>
              </Box>
            </Box>
            <Filters {...props} classes={classes} timeRangeFilterRef={timeRangeFilterRef} />
          </Box>
        </div>
      )}
    </>
  )
}

const SessionsFiltersBar = (props: ISessionsFiltersBar) => (
  <>
    <HiddenBox variant="hiddenSMDown">
      <DesktopFilters {...props} />
    </HiddenBox>
    <HiddenBox variant="hiddenSMUp">
      <MobileFilters {...props} />
    </HiddenBox>
  </>
)

export default SessionsFiltersBar
